import React, { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Settings, Trash2 } from "lucide-react";
import { useSimulationManagement } from "./useSimulationManagement";
import ManagementHeader from "../admin/managementHeader/ManagementHeader";
import type { simulation } from "../dashboard/types.api";
import AlertMessage from "@/components/layout/message/AlertMessage";
import DateRangeFilter from "./components/dateRangeFilter/DateRangeFilter";
import ActionPanel from "@/components/layout/actionPanel/ActionPanel";
import PaginationControls from "@/components/layout/paginationControls/PaginationControls";
import SimulationFormModal from "./components/simulationFormModal/SimulationFormModal";
import styles from "./SimulationManagement.module.css";
import { SimulationTable } from "./components/simulationTable/SimulationTable";
import type { Action } from "@/components/common/table/types.props";

const PAGE_SIZE = 15;

const SimulationManagement: React.FC = () => {
    const navigate = useNavigate();

    useEffect(() => {
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, []);

    const {
        simulations,
        count,
        currentPage,
        isLoading,
        isSubmitting,
        listError,
        isModalOpen,
        editingSimulation,
        actionError,
        apiFormError,
        searchTerm,
        setPage,
        handleAdd,
        handleEdit,
        handleDelete,
        handleSubmit,
        handleCloseModal,
        handleSearchChange,
        handleDateFilter,
    } = useSimulationManagement();

    const handleConfigure = (simulation: simulation) => {
        navigate(`/facilitator-panel/simulations/${simulation.id}/configure`);
    };

    const tableActions = useMemo<Action<simulation>[]>(
        () => [
            {
                label: "Configurar",
                handler: handleConfigure,
                variant: "primary",
                icon: <Settings size={16} />,
                disabled: (sim: simulation) => sim.state !== "in_design",
                tooltip: (sim: simulation) =>
                    sim.state !== "in_design"
                        ? "Solo se pueden configurar simulacros 'En diseño'"
                        : "Configurar pruebas y preguntas",
            },
            {
                label: "Modificar",
                handler: handleEdit,
                variant: "secondary",
                icon: <Edit size={16} />,
            },
            {
                label: "Eliminar",
                handler: handleDelete,
                variant: "danger",
                icon: <Trash2 size={16} />,
            },
        ],
        [handleEdit, handleDelete, handleConfigure],
    );

    return (
        <div className={styles["container-main"]}>
            <ManagementHeader />
            <h1 className={styles["container-title"]}>Gestión de simulacros</h1>

            <AlertMessage type="error" title="Error">
                {listError?.generalMessage || actionError?.generalMessage}
            </AlertMessage>

            <DateRangeFilter onFilter={handleDateFilter} />

            <ActionPanel
                placeholder="Buscar por nombre o descripción..."
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                actions={[
                    {
                        key: "add",
                        icon: <Plus size={16} />,
                        label: "Añadir Simulacro",
                        onClick: handleAdd,
                        variant: "primary",
                    },
                ]}
            />

            <SimulationTable
                data={simulations}
                isLoading={isLoading}
                actions={tableActions}
            />

            {count > PAGE_SIZE && (
                <PaginationControls
                    currentPage={currentPage}
                    totalCount={count}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                />
            )}

            {isModalOpen && (
                <SimulationFormModal
                    isOpen={isModalOpen}
                    initialData={editingSimulation || undefined}
                    isLoading={isSubmitting}
                    apiError={apiFormError}
                    onClose={handleCloseModal}
                    onSubmit={handleSubmit}
                />
            )}
        </div>
    );
};

export default SimulationManagement;
